import * as React from "react"
import SettingsOverviewCard from "../../molecules/SettingsOverviewCard"
import SatelliteIcon from "../../atoms/icons/SatelliteIcon"
import ReadOnlyTextField from "../../molecules/card/ReadOnlyTextField"
import { HorizontalSpacer } from "../../atoms/spacers"
import { Col, Row, Visible } from "react-grid-system"
import { AntennaOrientation, BeamList, Frequency, SignalQualityWithZero, LocalOrientation } from "../../../types"
import OrientationField from "../../molecules/card/OrientationField"
import LocalOrientationField from "../../molecules/card/LocalOrientationField"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import BeamSwitchIcon from "../../atoms/icons/BeamSwitchIcon"
import BeamSwitchModal from "../modals/BeamSwitchModal"
import { SettingsFieldDivider } from "../../atoms/card/SettingsField"
import SignalQualityChart from "../../molecules/charts/SignalQualityChart"
import CNChart from "../../molecules/charts/CNChart"
import BeaconChart from "../../molecules/charts/BeaconChart"
import { DeepPartial } from "edge-proto/dist/edge/v1/metrics"
import { SECTION_OVERVIEW_SATELLITE_SIGNAL } from "../../../utils/pages"
import { freqToString, formatFrequency } from "../../../utils/formatters"
import BeamMismatchModal from "../modals/BeamMismatchModal"

const SATELLITE_SIGNAL_TOOLTIP = `
C/N represents the strength of the data signal from the satellite, measured relative to the background noise. 
Beacon represents the strenght of the received beacon signal from the satellite, used to track the satellite. 
The current orientation indicates where the antenna is pointing. 
This can be compared to the target orientation, which is where the antenna should be pointing.
`

interface SatelliteSignalCardProps {
  currentOrientation?: DeepPartial<AntennaOrientation>
  targetOrientation?: DeepPartial<AntennaOrientation>
  targetLocalOrientation?: DeepPartial<LocalOrientation>
  motorLocalOrientation?: DeepPartial<LocalOrientation>
  signalQuality?: SignalQualityWithZero
  powerDetSignal?: number
  cOverN?: number
  highestCN?: number
  rf?: Frequency
  lo?: Frequency
  bandwidth?: Frequency
  configuredBeam?: [string, number]
  activeBeam?: [string, number]
  longitudeSatellite?: string
  beams: BeamList | undefined
  beamSwitchModalOpen: boolean
  setBeamSwitchModalOpen: (open: boolean) => void
  showBeamSwitch?: boolean
}

const SatelliteSignalCard: React.FunctionComponent<SatelliteSignalCardProps> = ({
  currentOrientation,
  targetOrientation,
  targetLocalOrientation,
  motorLocalOrientation,
  cOverN,
  powerDetSignal,
  signalQuality,
  rf,
  lo,
  bandwidth,
  configuredBeam,
  activeBeam,
  longitudeSatellite,
  beams,
  beamSwitchModalOpen,
  setBeamSwitchModalOpen,
  showBeamSwitch = true,
}) => {
  const [beamMismatchDismissed, setBeamMismatchDismissed] = React.useState(false)
  const beamMismatch = configuredBeam !== undefined && activeBeam !== undefined && configuredBeam[1] !== activeBeam[1]
  const showMismatchModal = beamMismatch && !beamMismatchDismissed

  React.useEffect(() => {
    if (beamMismatch) {
      setBeamMismatchDismissed(false)
    }
  }, [beamMismatch])

  return (
    <SettingsOverviewCard
      title={SECTION_OVERVIEW_SATELLITE_SIGNAL.name}
      icon={<SatelliteIcon />}
      tooltip={SATELLITE_SIGNAL_TOOLTIP}
    >
      <BeamMismatchModal
        open={showMismatchModal}
        onClose={() => setBeamMismatchDismissed(true)}
        activeBeam={activeBeam?.[0] ?? ""}
      />

      <Row justify="between" align="center">
        <SignalQualityChart signalQuality={signalQuality} />
        <CNChart cOverN={cOverN} />
        <BeaconChart powerDetSignal={powerDetSignal} />
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between">
        <Col>
          <OrientationField title="Current orientation" orientation={currentOrientation} />
          <Visible xs sm>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col>
          <OrientationField title="Target orientation" orientation={targetOrientation} />
          <Visible xs sm>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row justify="between">
        <Col>
          <LocalOrientationField title="Motor local orientation" orientation={motorLocalOrientation} />
          <Visible xs sm>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col>
          <LocalOrientationField title="Target local orientation" orientation={targetLocalOrientation} />
          <Visible xs sm>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between">
        <Col sm={12} md={6}>
          <ReadOnlyTextField label="Beam ID" value={activeBeam?.[0]} placeholder="----------" textAlign="right" />
          <ReadOnlyTextField
            label="IF frequency"
            value={freqToString(rf?.rx, "MHz", "rx")}
            placeholder={formatFrequency("--", "MHz", "rx")}
            textAlign="right"
          />
        </Col>
        <Visible xs sm>
          <SettingsFieldDivider />
        </Visible>
        <Col sm={12} md={6}>
          <ReadOnlyTextField
            label="Bandwidth"
            value={freqToString(bandwidth?.rx, "MHz", "rx")}
            placeholder={formatFrequency("--", "MHz", "rx")}
            secondPlaceholder=""
            textAlign="right"
          />
          <ReadOnlyTextField
            label="LO frequency"
            value={freqToString(lo?.rx, "GHz", "rx")}
            secondValue={freqToString(lo?.tx, "GHz", "tx")}
            placeholder={formatFrequency("--", "GHz", "rx")}
            secondPlaceholder={formatFrequency("--", "GHz", "tx")}
            textAlign="right"
          />
        </Col>
      </Row>
      {showBeamSwitch && (
        <>
          <HorizontalSpacer spacing={2} />
          <PrimaryButton
            title="Beam switch"
            iconLeft={<BeamSwitchIcon />}
            fullWidth
            onClick={() => setBeamSwitchModalOpen(true)}
          />
          <BeamSwitchModal beams={beams} open={beamSwitchModalOpen} onClose={() => setBeamSwitchModalOpen(false)} />
        </>
      )}
    </SettingsOverviewCard>
  )
}

export default SatelliteSignalCard
